'use client';

import { useEffect, useRef } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { AiToolkitSubmitStatus, VideoEditSubmitStatus } from '../types';

export const POLL_INTERVAL_MS = 5000;
// 11 minutes at 5s intervals: one minute past StockBlocks' 10-minute CheckAiTaskStatusJob window,
// so the shell observes the backend's `timed_out` FAILED task instead of timing out first.
export const MAX_ATTEMPTS = 132;

export type PollResult = 'continue' | 'success' | 'error';

export type UseGenerationPollingConfig = {
    taskId: string | null;
    // Only 'inProgress' polls; video edit's extra pre-submit 'selection' status is accepted too.
    status: AiToolkitSubmitStatus | VideoEditSubmitStatus;
    onPoll: (taskId: string) => Promise<PollResult>;
    onTimeout: () => void;
};

const logger = LoggerFactory.create('useGenerationPolling');

export function useGenerationPolling({ taskId, status, onPoll, onTimeout }: UseGenerationPollingConfig): void {
    const attemptsRef = useRef(0);

    const onPollRef = useRef(onPoll);

    const onTimeoutRef = useRef(onTimeout);

    useEffect(() => {
        onPollRef.current = onPoll;
        onTimeoutRef.current = onTimeout;
    });

    useEffect(() => {
        if (!taskId || status !== 'inProgress') {
            attemptsRef.current = 0;
            return;
        }

        let cancelled = false;

        const poll = async (): Promise<void> => {
            if (cancelled) return;
            attemptsRef.current += 1;

            if (attemptsRef.current > MAX_ATTEMPTS) {
                onTimeoutRef.current();
                window.clearInterval(intervalId);
                return;
            }

            try {
                const result = await onPollRef.current(taskId);

                if (cancelled) return;

                if (result === 'success' || result === 'error') {
                    window.clearInterval(intervalId);
                }
            } catch (error) {
                // transient errors are ignored; max-attempts guard ends the loop
                logger.warn({ error }, 'Error during generation polling');
            }
        };

        void poll();

        const intervalId = window.setInterval(() => {
            void poll();
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [taskId, status]);
}
